import type { PixelProvider } from "@codway-dev/shared";

// ---------------------------------------------------------------------------
// Client-side leg of the "Probar" button. Loads the provider SDK inside the
// embedded admin app and fires ONE clearly-branded custom test event so the
// merchant can confirm delivery in the provider's debug view (Meta/TikTok Test
// Events, GA4 DebugView/Realtime).
//
// There is no programmatic success here — a browser event gives no ack, so the
// caller just tells the merchant to verify in the provider. Because the event
// originates from admin.shopify.com, the pixel's traffic-permission allow-list
// must include that domain or Meta drops it (surfaced as a warning in the UI).
//
// A trimmed port of the storefront widget's pixelTracking SDK bootstrap; only
// init + a single custom event is needed here.
// ---------------------------------------------------------------------------

const META_TEST_EVENT = "CodwayPixelTest";
const TIKTOK_TEST_EVENT = "CodwayPixelTest";
const GOOGLE_TEST_EVENT = "codway_pixel_test";

type FbqFn = (...args: unknown[]) => void;
type GtagFn = (...args: unknown[]) => void;

/* eslint-disable @typescript-eslint/no-explicit-any */
interface PixelWindow {
  fbq?: FbqFn;
  _fbq?: FbqFn;
  gtag?: GtagFn;
  dataLayer?: unknown[];
  ttq?: any;
  TiktokAnalyticsObject?: string;
}

function injectScript(src: string, marker: string): void {
  if (document.getElementById(marker)) return;
  const el = document.createElement("script");
  el.async = true;
  el.src = src;
  el.id = marker;
  document.head.appendChild(el);
}

const metaInited = new Set<string>();
function fireMeta(w: PixelWindow, pixelId: string): void {
  if (!w.fbq) {
    const fbq: FbqFn = function () {
      // eslint-disable-next-line prefer-rest-params
      (fbq as unknown as { queue: unknown[] }).queue.push(arguments);
    };
    (fbq as unknown as { queue: unknown[] }).queue = [];
    w.fbq = fbq;
    w._fbq = w._fbq || fbq;
    injectScript(
      "https://connect.facebook.net/en_US/fbevents.js",
      "codway-fbq"
    );
  }
  if (!metaInited.has(pixelId)) {
    w.fbq("init", pixelId);
    metaInited.add(pixelId);
  }
  // trackSingleCustom keeps the test off the standard-event conversion reports.
  w.fbq("trackSingleCustom", pixelId, META_TEST_EVENT, {});
}

const googleInited = new Set<string>();
function fireGoogle(w: PixelWindow, measurementId: string): void {
  if (!w.gtag) {
    w.dataLayer = w.dataLayer || [];
    const gtag: GtagFn = function () {
      // eslint-disable-next-line prefer-rest-params
      w.dataLayer!.push(arguments);
    };
    w.gtag = gtag;
    w.gtag("js", new Date());
    injectScript(
      `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(measurementId)}`,
      "codway-gtag"
    );
  }
  if (!googleInited.has(measurementId)) {
    w.gtag("config", measurementId, { send_page_view: false });
    googleInited.add(measurementId);
  }
  w.gtag("event", GOOGLE_TEST_EVENT, {
    send_to: measurementId,
    debug_mode: true,
  });
}

const tiktokInited = new Set<string>();
function fireTikTok(w: PixelWindow, pixelId: string): void {
  /* eslint-disable prefer-rest-params */
  if (!w.ttq) {
    w.TiktokAnalyticsObject = "ttq";
    const ttq: any = (w.ttq = []);
    ttq.methods = [
      "page",
      "track",
      "identify",
      "instances",
      "debug",
      "on",
      "off",
      "once",
      "ready",
      "alias",
      "group",
      "enableCookie",
      "disableCookie",
      "holdConsent",
      "revokeConsent",
      "grantConsent",
    ];
    ttq.setAndDefer = function (t: any, e: string) {
      t[e] = function () {
        t.push([e].concat(Array.prototype.slice.call(arguments, 0)));
      };
    };
    for (let i = 0; i < ttq.methods.length; i++) {
      ttq.setAndDefer(ttq, ttq.methods[i]);
    }
    ttq.instance = function (t: string) {
      const inst = ttq._i[t] || [];
      for (let n = 0; n < ttq.methods.length; n++) {
        ttq.setAndDefer(inst, ttq.methods[n]);
      }
      return inst;
    };
    ttq.load = function (e: string) {
      const url = "https://analytics.tiktok.com/i18n/pixel/events.js";
      ttq._i = ttq._i || {};
      ttq._i[e] = [];
      ttq._i[e]._u = url;
      ttq._t = ttq._t || {};
      ttq._t[e] = +new Date();
      ttq._o = ttq._o || {};
      ttq._o[e] = {};
      const script = document.createElement("script");
      script.type = "text/javascript";
      script.async = true;
      script.src = `${url}?sdkid=${e}&lib=ttq`;
      const first = document.getElementsByTagName("script")[0];
      if (first && first.parentNode) {
        first.parentNode.insertBefore(script, first);
      } else {
        document.head.appendChild(script);
      }
    };
  }
  if (!tiktokInited.has(pixelId)) {
    w.ttq.load(pixelId);
    tiktokInited.add(pixelId);
  }
  w.ttq.instance(pixelId).track(TIKTOK_TEST_EVENT, {});
  /* eslint-enable prefer-rest-params */
}
/* eslint-enable @typescript-eslint/no-explicit-any */

/**
 * Fires a client-side test event for the given saved pixel. Best-effort and
 * fire-and-forget: there is no provider ack on the browser, so the caller must
 * direct the merchant to the provider's debug view to confirm.
 */
export function fireClientTestEvent(
  provider: PixelProvider,
  pixelId: string
): void {
  if (typeof window === "undefined") return;
  const w = window as unknown as PixelWindow;
  try {
    if (provider === "META") fireMeta(w, pixelId);
    else if (provider === "GOOGLE") fireGoogle(w, pixelId);
    else if (provider === "TIKTOK") fireTikTok(w, pixelId);
  } catch (err) {
    console.warn("[pixel-test] client-side test event failed", provider, err);
  }
}
